import React, { useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "configureStore";
import {
  Layout,
  Page,
  Card,
  Text,
  BlockStack,
  Spinner,
  Button,
  TextContainer,
  useBreakpoints,
} from "@shopify/polaris";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  fetchCompanyDetails,
  setDestroy,
} from "containers/settings/settings-container.slice";
import UserList from "features/user-list/user-list";
import CompanyAboutSkeleton from "./company-about-skeleton";

function CompanyAbout() {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  // Below Polaris's `lg` width (1040 px) the page is one column. Polaris would put two columns side by side
  // from about 800 px, so both sections are forced to full width, otherwise the reordered columns would swap places.
  const { lgDown: isLayoutStacked } = useBreakpoints();
  const { companyId } = useParams();

  const loadCompanyData = useCallback(() => {
    if (companyId) {
      dispatch(fetchCompanyDetails({ companyId }));
    }
  }, [companyId, dispatch]);

  const { companyEntity, isLoading, isError } = useSelector(
    (state: RootState) => state.companyContainerReducer
  );

  useEffect(() => {
    loadCompanyData();
    return () => {
      dispatch(setDestroy());
    };
  }, [loadCompanyData]);

  if (!companyEntity) {
    // Show an error with a retry when the fetch failed (and isn't in flight)
    // instead of an endless skeleton; otherwise show the skeleton while loading.
    if (isError && !isLoading) {
      return (
        <Page fullWidth title={t("about_company", { defaultValue: "Company" })}>
          <Card>
            <BlockStack gap="300">
              <Text as="p" tone="critical">
                {t("not_fetch", { defaultValue: "Could not load company" })}
              </Text>
              <div>
                <Button onClick={loadCompanyData}>
                  {t("retry", { defaultValue: "Retry" })}
                </Button>
              </div>
            </BlockStack>
          </Card>
        </Page>
      );
    }
    return <CompanyAboutSkeleton />;
  }

  const peopleSection = (
    <Layout.Section variant={isLayoutStacked ? "fullWidth" : undefined}>
      <BlockStack gap="500">
        <UserList source={"about-company"} />
      </BlockStack>
    </Layout.Section>
  );

  return (
    <Page
      fullWidth
      title={companyEntity.name}
      titleMetadata={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {isLoading && <Spinner size="small" />}
        </div>
      }
    >
      <Layout>
        {/* Stacked, the company's details come first; otherwise they sit under a list of hundreds of people. */}
        {!isLayoutStacked && peopleSection}
        <Layout.Section variant={isLayoutStacked ? "fullWidth" : "oneThird"}>
          <BlockStack gap="500">
            <Card roundedAbove="sm">
              <TextContainer>
                <Text as="p" variant="bodyMd">
                  <strong>{t("title")}:</strong> {companyEntity?.name}
                </Text>
                {companyEntity.legalName ? (
                  <Text as="p" variant="bodyMd">
                    <strong>{t("legal_name")}:</strong>{" "}
                    {companyEntity.legalName}
                  </Text>
                ) : (
                  <Text as="p" variant="bodyMd">
                    {t("no_legal_name_provided")}
                  </Text>
                )}
                {companyEntity.vat ? (
                  <Text as="p" variant="bodyMd">
                    <strong>{t("company_vat")}:</strong> {companyEntity.vat}
                  </Text>
                ) : (
                  <Text as="p" variant="bodyMd">
                    {t("no_vat_provided")}
                  </Text>
                )}
                {companyEntity.code ? (
                  <Text as="p" variant="bodyMd">
                    <strong>{t("company_code")}:</strong> {companyEntity.code}
                  </Text>
                ) : (
                  <Text as="p" variant="bodyMd">
                    {t("no_code_provided")}
                  </Text>
                )}
                {companyEntity.address ? (
                  <Text as="p" variant="bodyMd">
                    <strong>{t("address")}:</strong> {companyEntity.address}
                  </Text>
                ) : (
                  <Text as="p" variant="bodyMd">
                    {t("no_address_provided")}
                  </Text>
                )}
                {companyEntity.description ? (
                  <Text as="p" variant="bodyMd">
                    <strong>{t("description")}:</strong>{" "}
                    {companyEntity.description}
                  </Text>
                ) : (
                  <Text as="p" variant="bodyMd">
                    {t("no_description_provided")}
                  </Text>
                )}
              </TextContainer>
            </Card>
          </BlockStack>
        </Layout.Section>
        {isLayoutStacked && peopleSection}
      </Layout>
    </Page>
  );
}

export default CompanyAbout;
